/**
 * Enhanced Map Visualization using Mapbox GL
 * Converts analysis results into interactive map visualizations
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Map, { Source, Layer, Marker, Popup, ScaleControl, MapRef } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  Divider,
  Card,
  useTheme,
  IconButton,
  Tooltip,
  Grid,
  alpha,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Mouse as MouseIcon,
  Add as ZoomInIcon,
  Remove as ZoomOutIcon,
  Navigation as CompassIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  MyLocation as MyLocationIcon,
  Satellite as SatelliteIcon,
  Straighten as MeasureIcon,
  Delete as DeleteIcon,
  Build as MapToolsIcon,
  ChevronLeft as CollapseToolsIcon,
} from '@mui/icons-material';
import { MapStyleType } from '@signals/cop-ui/components/MapStyleControl';

import { DwellLocation, Route } from '@signals/app-shell/types';
import {
  DARK_MAP_POPUP_CLASS,
  DarkMapPopupShell,
  DarkMapPopupRow,
  DarkMapPopupCoords,
  useDarkMapPopupChrome,
} from '@signals/app-shell/components/map/DarkMapPopup';

interface EnhancedMapVisualizationGLProps {
  dwellLocations: DwellLocation[];
  routes: Route[];
  heatmapData?: Array<{
    lat: number;
    lon: number;
    intensity: number;
    timestamp?: string;
    hour?: number;
    is_daytime?: boolean;
    entity_id?: string;
  }>;
  daytimeHeatmapData?: Array<{
    lat: number;
    lon: number;
    intensity: number;
  }>;
  nighttimeHeatmapData?: Array<{
    lat: number;
    lon: number;
    intensity: number;
  }>;
  deviceTracks?: Record<string, {
    coordinates: [number, number][];
    point_count: number;
    device_type: string;
  }>;
  temporalBreakdown?: {
    daytime_points: number;
    nighttime_points: number;
    total_points: number;
  };
  activeTab?: number;
  selectedDwellId?: number | null;
  selectedRouteId?: number | null;
  /** Override fixed 600px map height (e.g. "100%" for report iframe embed). */
  mapHeight?: string | number;
  /** Collapse the layer panel and hide empty toggles — used by POL report tabs. */
  compactControls?: boolean;
}

interface LayerState {
  dwellLocations: boolean;
  highFreqRoutes: boolean;
  medFreqRoutes: boolean;
  lowFreqRoutes: boolean;
  allDeviceTracks: boolean;
  heatmap: boolean;
  daytimeActivity: boolean;
  nighttimeActivity: boolean;
}

type DeviceTrackEntry = NonNullable<EnhancedMapVisualizationGLProps['deviceTracks']>[string];

type HeatmapDatum = NonNullable<EnhancedMapVisualizationGLProps['heatmapData']>[number];
type DayNightHeatmapDatum = NonNullable<EnhancedMapVisualizationGLProps['daytimeHeatmapData']>[number];

/** Default when no data yet: continental US, not a specific city. */
const NEUTRAL_US_VIEW = { longitude: -98, latitude: 39.5, zoom: 3 };

/** [lon, lat] — used to frame the map from every spatial layer, not just dwells. */
function collectMapPoints(params: {
  dwells: DwellLocation[];
  routes: Route[];
  heatmapData: HeatmapDatum[];
  daytimeHeatmapData: DayNightHeatmapDatum[];
  nighttimeHeatmapData: DayNightHeatmapDatum[];
  deviceTracks: EnhancedMapVisualizationGLProps['deviceTracks'];
}): [number, number][] {
  const out: [number, number][] = [];
  const add = (lon: number, lat: number) => {
    if (
      Number.isFinite(lon) && Number.isFinite(lat) &&
      lon >= -180 && lon <= 180 &&
      lat >= -90 && lat <= 90
    ) {
      out.push([lon, lat]);
    }
  };
  for (const location of params.dwells) {
    if (location == null) continue;
    add(location.center_longitude, location.center_latitude);
  }
  for (const route of params.routes) {
    if (route.path_coordinates && Array.isArray(route.path_coordinates)) {
      for (const coord of route.path_coordinates) {
        if (coord && coord.length >= 2) {
          add(coord[1] as number, coord[0] as number);
        }
      }
    }
  }
  for (const p of params.heatmapData) {
    if (p && typeof p.lon === 'number' && typeof p.lat === 'number') {
      add(p.lon, p.lat);
    }
  }
  for (const p of params.daytimeHeatmapData) {
    if (p && typeof p.lon === 'number' && typeof p.lat === 'number') {
      add(p.lon, p.lat);
    }
  }
  for (const p of params.nighttimeHeatmapData) {
    if (p && typeof p.lon === 'number' && typeof p.lat === 'number') {
      add(p.lon, p.lat);
    }
  }
  for (const track of Object.values(params.deviceTracks || {})) {
    if (track?.coordinates) {
      for (const c of track.coordinates) {
        if (c && c.length >= 2) {
          add(c[0] as number, c[1] as number);
        }
      }
    }
  }
  return out;
}

const EnhancedMapVisualizationGL: React.FC<EnhancedMapVisualizationGLProps> = ({
  dwellLocations,
  routes,
  heatmapData = [] as HeatmapDatum[],
  daytimeHeatmapData = [] as DayNightHeatmapDatum[],
  nighttimeHeatmapData = [] as DayNightHeatmapDatum[],
  deviceTracks = {},
  temporalBreakdown,
  activeTab,
  selectedDwellId,
  selectedRouteId,
  mapHeight = '600px',
  compactControls = false,
}) => {
  const theme = useTheme();
  // Map style based on theme: light mode = streets, dark mode = dark
  const [mapStyle, setMapStyle] = useState<MapStyleType>(() => 
    theme.palette.mode === 'light' ? 'streets' : 'dark'
  );
  const [scrollWheelZoom, setScrollWheelZoom] = useState<boolean>(false);
  // Map tools collapse to a single icon, matching the Pathfinder tool rail, so
  // the panel stops covering the map on small report tiles.
  const [mapToolsOpen, setMapToolsOpen] = useState<boolean>(true);
  const [selectedDwell, setSelectedDwell] = useState<DwellLocation | null>(null);
  useDarkMapPopupChrome(!!selectedDwell);
  const mapRef = useRef<MapRef | null>(null);
  const geocoderRef = useRef<MapboxGeocoder | null>(null);
  const allPointsForMapRef = useRef<[number, number][]>([]);
  const lastFittedDataSig = useRef<string | null>(null);

  // Define layer presets for each tab
  const getLayerPresets = (tabIndex?: number): LayerState => {
    switch (tabIndex) {
      case 0: // Overview
        return {
          dwellLocations: true,
          highFreqRoutes: false,
          medFreqRoutes: false,
          lowFreqRoutes: false,
          allDeviceTracks: true,
          heatmap: true,
          daytimeActivity: false,
          nighttimeActivity: false,
        };
      case 1: // Dwell Analysis
        return {
          dwellLocations: true,
          highFreqRoutes: false,
          medFreqRoutes: false,
          lowFreqRoutes: false,
          allDeviceTracks: false,
          heatmap: false,
          daytimeActivity: false,
          nighttimeActivity: false,
        };
      case 2: // Route Patterns
        return {
          dwellLocations: true,
          highFreqRoutes: true,
          medFreqRoutes: true,
          lowFreqRoutes: true,
          allDeviceTracks: false,
          heatmap: false,
          daytimeActivity: false,
          nighttimeActivity: false,
        };
      case 3: // Day vs Night Activity
        return {
          dwellLocations: true,
          highFreqRoutes: false,
          medFreqRoutes: false,
          lowFreqRoutes: false,
          allDeviceTracks: false,
          heatmap: false,
          daytimeActivity: true,
          nighttimeActivity: true,
        };
      default:
        return {
          dwellLocations: true,
          highFreqRoutes: false,
          medFreqRoutes: false,
          lowFreqRoutes: false,
          allDeviceTracks: true,
          heatmap: true,
          daytimeActivity: false,
          nighttimeActivity: false,
        };
    }
  };

  const [layers, setLayers] = useState<LayerState>(getLayerPresets(activeTab));
  const [viewport, setViewport] = useState(NEUTRAL_US_VIEW);
  const [mapLoaded, setMapLoaded] = useState(false);
  
  // Map tool states
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [measurementPoints, setMeasurementPoints] = useState<[number, number][]>([]);
  const [totalDistance, setTotalDistance] = useState<number>(0);
  const [styleAnchorEl, setStyleAnchorEl] = useState<HTMLElement | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);

  // Update layers when activeTab changes
  useEffect(() => {
    if (activeTab !== undefined) {
      setLayers(getLayerPresets(activeTab));
    }
  }, [activeTab]);

  // Filter data based on selected table rows
  const filteredDwellLocations = selectedDwellId
    ? dwellLocations.filter(dwell => dwell.id === selectedDwellId)
    : dwellLocations;

  const filteredRoutes = selectedRouteId
    ? routes.filter(route => route.id === selectedRouteId)
    : routes;

  // All [lon, lat] from filtered tables + every spatial layer (routes/heatmap/tracks without requiring dwells).
  const allMapPoints = useMemo(
    () =>
      collectMapPoints({
        dwells: filteredDwellLocations,
        routes: filteredRoutes,
        heatmapData,
        daytimeHeatmapData,
        nighttimeHeatmapData,
        deviceTracks,
      }),
    [
      filteredDwellLocations,
      filteredRoutes,
      heatmapData,
      daytimeHeatmapData,
      nighttimeHeatmapData,
      deviceTracks,
    ]
  );

  allPointsForMapRef.current = allMapPoints;

  // After the map is ready, fit to all relevant points. Must not require dwells (heatmaps/routes alone are valid)
  // and must re-run when `mapLoaded` flips, since the first run may have seen mapRef null.
  // Skip duplicate fits when the parent re-renders with a new `[]` reference for the same geometry.
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    if (allMapPoints.length === 0) {
      lastFittedDataSig.current = null;
      return;
    }

    let minLon = Infinity, maxLon = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;
    for (const [lon, lat] of allMapPoints) {
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }

    const dataSig = [
      allMapPoints.length,
      minLon.toFixed(5),
      maxLon.toFixed(5),
      minLat.toFixed(5),
      maxLat.toFixed(5),
    ].join('|');
    if (dataSig === lastFittedDataSig.current) {
      return;
    }
    lastFittedDataSig.current = dataSig;

    const map = mapRef.current;
    const DEG = 0.02; // ~2 km — avoid degenerate single-point or thin bounds

    if (allMapPoints.length === 1) {
      const [lon, lat] = allMapPoints[0];
      map.flyTo({ center: [lon, lat], zoom: 12, duration: 0 });
      return;
    }

    if (maxLon - minLon < DEG) {
      const m = (minLon + maxLon) / 2;
      minLon = m - DEG / 2;
      maxLon = m + DEG / 2;
    }
    if (maxLat - minLat < DEG) {
      const m = (minLat + maxLat) / 2;
      minLat = m - DEG / 2;
      maxLat = m + DEG / 2;
    }

    minLon = Math.max(minLon, -180);
    maxLon = Math.min(maxLon, 180);
    minLat = Math.max(minLat, -90);
    maxLat = Math.min(maxLat, 90);

    const bounds: [[number, number], [number, number]] = [
      [minLon, minLat],
      [maxLon, maxLat],
    ];

    map.fitBounds(bounds, { padding: 50, duration: 0, maxZoom: 16 });
  }, [mapLoaded, allMapPoints]);

  // Add geocoder on map load
  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (map && !geocoderRef.current) {
      const pts = allPointsForMapRef.current;
      const proximity =
        pts.length > 0
          ? {
              longitude: pts.reduce((a, p) => a + p[0], 0) / pts.length,
              latitude: pts.reduce((a, p) => a + p[1], 0) / pts.length,
            }
          : { longitude: NEUTRAL_US_VIEW.longitude, latitude: NEUTRAL_US_VIEW.latitude };
      const geocoder = new MapboxGeocoder({
        accessToken: process.env.REACT_APP_MAPBOX_TOKEN || '',
        mapboxgl: require('mapbox-gl') as any,
        marker: false,
        placeholder: 'Search for address or place...',
        proximity: proximity as any,
      });

      map.addControl(geocoder, 'top-left');
      geocoderRef.current = geocoder;
    }
    setMapLoaded(true);
  }, []);

  // Toggle layer visibility
  const toggleLayer = (layerKey: keyof LayerState) => {
    setLayers(prev => ({
      ...prev,
      [layerKey]: !prev[layerKey]
    }));
  };

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    mapRef.current?.getMap().zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    mapRef.current?.getMap().zoomOut();
  }, []);

  const handleResetBearing = useCallback(() => {
    mapRef.current?.getMap().resetNorth();
  }, []);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!mapContainerRef.current) return;
    
    if (!document.fullscreenElement) {
      mapContainerRef.current.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  // Listen for fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Calculate distance for measurement tool (returns km)
  const calculateMeasurementDistance = useCallback((lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  // Update total distance when measurement points change
  useEffect(() => {
    if (measurementPoints.length < 2) {
      setTotalDistance(0);
      return;
    }
    
    let total = 0;
    for (let i = 1; i < measurementPoints.length; i++) {
      total += calculateMeasurementDistance(
        measurementPoints[i - 1][1], measurementPoints[i - 1][0],
        measurementPoints[i][1], measurementPoints[i][0]
      );
    }
    setTotalDistance(total);
  }, [measurementPoints, calculateMeasurementDistance]);

  // Clear measurement
  const clearMeasurement = useCallback(() => {
    setMeasurementPoints([]);
    setTotalDistance(0);
  }, []);

  // Toggle measurement mode
  const toggleMeasurement = useCallback(() => {
    if (isMeasuring) {
      setIsMeasuring(false);
    } else {
      setIsMeasuring(true);
      clearMeasurement();
    }
  }, [isMeasuring, clearMeasurement]);

  // Format distance for display
  const formatDistance = useCallback((km: number): string => {
    if (km < 1) {
      return `${(km * 1000).toFixed(0)} m`;
    }
    return `${km.toFixed(2)} km (${(km * 0.621371).toFixed(2)} mi)`;
  }, []);

  // Handle map click for measurement
  const handleMapClick = useCallback((event: any) => {
    if (isMeasuring) {
      const { lng, lat } = event.lngLat;
      setMeasurementPoints(prev => [...prev, [lng, lat]]);
    }
  }, [isMeasuring]);

  // Categorize routes by frequency
  const getRoutesByFrequency = (minFrequency: number) => {
    return filteredRoutes.filter(route => (route.frequency || 0) >= minFrequency);
  };

  const highFreqRoutes = getRoutesByFrequency(10);
  const medFreqRoutes = filteredRoutes.filter(r => (r.frequency || 0) >= 5 && (r.frequency || 0) < 10);
  const lowFreqRoutes = filteredRoutes.filter(r => (r.frequency || 0) < 5);

  // Create GeoJSON for routes
  const routesGeoJSON = useMemo(() => {
    const features: any[] = [];

    const routesToShow = [
      ...(layers.highFreqRoutes ? highFreqRoutes : []),
      ...(layers.medFreqRoutes ? medFreqRoutes : []),
      ...(layers.lowFreqRoutes ? lowFreqRoutes : [])
    ];

    routesToShow.forEach(route => {
      if (route.path_coordinates && route.path_coordinates.length > 0) {
        features.push({
          type: 'Feature' as const,
          geometry: {
            type: 'LineString' as const,
            coordinates: route.path_coordinates.map(coord => [coord[1], coord[0]]) // [lon, lat]
          },
          properties: {
            id: route.id,
            frequency: route.frequency || 0,
            color: (route.frequency || 0) >= 10 ? '#E74C3C' : (route.frequency || 0) >= 5 ? '#F39C12' : '#3498DB'
          }
        });
      }
    });

    return { type: 'FeatureCollection' as const, features };
  }, [filteredRoutes, layers, highFreqRoutes, medFreqRoutes, lowFreqRoutes]);

  // Create GeoJSON for device tracks
  const deviceTracksGeoJSON = useMemo(() => {
    if (!layers.allDeviceTracks) return { type: 'FeatureCollection' as const, features: [] };

    const features: any[] = [];

    (Object.entries(deviceTracks || {}) as [string, DeviceTrackEntry][]).forEach(([deviceId, track]) => {
      if (track.coordinates && track.coordinates.length > 0) {
        features.push({
          type: 'Feature' as const,
          geometry: {
            type: 'LineString' as const,
            coordinates: track.coordinates
          },
          properties: {
            deviceId,
            deviceType: track.device_type,
            pointCount: track.point_count
          }
        });
      }
    });

    return { type: 'FeatureCollection' as const, features };
  }, [deviceTracks, layers.allDeviceTracks]);

  // Create GeoJSON for heatmap
  const heatmapGeoJSON = useMemo(() => {
    if (!layers.heatmap || heatmapData.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features = heatmapData.map(point => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [point.lon, point.lat]
      },
      properties: {
        intensity: point.intensity
      }
    }));

    return { type: 'FeatureCollection' as const, features };
  }, [heatmapData, layers.heatmap]);

  // Create GeoJSON for daytime activity
  const daytimeHeatmapGeoJSON = useMemo(() => {
    if (!layers.daytimeActivity || daytimeHeatmapData.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features = daytimeHeatmapData.map(point => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [point.lon, point.lat]
      },
      properties: {
        intensity: point.intensity
      }
    }));

    return { type: 'FeatureCollection' as const, features };
  }, [daytimeHeatmapData, layers.daytimeActivity]);

  // Create GeoJSON for nighttime activity
  const nighttimeHeatmapGeoJSON = useMemo(() => {
    if (!layers.nighttimeActivity || nighttimeHeatmapData.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features = nighttimeHeatmapData.map(point => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [point.lon, point.lat]
      },
      properties: {
        intensity: point.intensity
      }
    }));

    return { type: 'FeatureCollection' as const, features };
  }, [nighttimeHeatmapData, layers.nighttimeActivity]);

  // Get dwell icon color and size based on visits
  const getDwellIconStyle = (visits: number) => {
    if (visits > 20) return { color: '#E74C3C', size: 32 };
    if (visits > 10) return { color: '#F39C12', size: 28 };
    if (visits > 5) return { color: '#3498DB', size: 24 };
    return { color: '#95A5A6', size: 20 };
  };

  const resolvedMapStyle = useMemo(() => {
    const styleMap: Record<MapStyleType, string> = {
      'streets': 'mapbox://styles/mapbox/streets-v12',
      'satellite': 'mapbox://styles/mapbox/satellite-v9',
      'satellite-streets': 'mapbox://styles/mapbox/satellite-streets-v12',
      'light': 'mapbox://styles/mapbox/light-v11',
      'dark': 'mapbox://styles/mapbox/dark-v11',
      'outdoors': 'mapbox://styles/mapbox/outdoors-v12',
    };
    return styleMap[mapStyle];
  }, [mapStyle]);

  return (
    <Box ref={mapContainerRef} sx={{ height: mapHeight, position: 'relative', minHeight: mapHeight === '100%' ? 700 : mapHeight }}>
      {/* Map Container */}
      <Card sx={{ height: '100%', width: '100%' }}>
        <Box sx={{ height: '100%', position: 'relative' }}>
          <Map
            ref={mapRef}
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
            longitude={viewport.longitude}
            latitude={viewport.latitude}
            zoom={viewport.zoom}
            onMove={(evt) => setViewport(evt.viewState)}
            onClick={handleMapClick}
            mapStyle={resolvedMapStyle}
            projection={{name: 'mercator'}}
            preserveDrawingBuffer={true}
            style={{ 
              width: '100%', 
              height: '100%', 
              minHeight: mapHeight === '100%' ? '100%' : mapHeight,
              cursor: isMeasuring ? 'crosshair' : 'auto'
            }}
            scrollZoom={scrollWheelZoom}
            onLoad={handleMapLoad}
          >
            {/* Scale Control */}
            <ScaleControl position="bottom-left" />

            {/* Unified Map Tools Panel - Left Side */}
            <Paper
              elevation={2}
              sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : 'none',
              }}
            >
              {/* Collapse / expand toggle — keeps the rail idiom from the
                  Pathfinder tool tabs: one icon when closed, full stack when
                  open. */}
              <Tooltip title={mapToolsOpen ? 'Hide map tools' : 'Show map tools'} placement="right">
                <IconButton
                  onClick={() => {
                    setMapToolsOpen((open) => !open);
                    setStyleAnchorEl(null);
                  }}
                  size="small"
                  aria-label={mapToolsOpen ? 'Hide map tools' : 'Show map tools'}
                  aria-expanded={mapToolsOpen}
                  sx={{
                    borderRadius: 0,
                    p: 1,
                    color: theme.palette.mode === 'dark' ? 'white' : 'inherit',
                  }}
                >
                  {mapToolsOpen
                    ? <CollapseToolsIcon fontSize="small" />
                    : <MapToolsIcon fontSize="small" />}
                </IconButton>
              </Tooltip>

              {mapToolsOpen && (
                <Divider sx={{ borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'inherit' }} />
              )}

              {mapToolsOpen && (
                <>
              {/* Zoom In */}
              <Tooltip title="Zoom In" placement="right">
                <IconButton onClick={handleZoomIn} size="small" sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Zoom Out */}
              <Tooltip title="Zoom Out" placement="right">
                <IconButton onClick={handleZoomOut} size="small" sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Reset North */}
              <Tooltip title="Reset North" placement="right">
                <IconButton onClick={handleResetBearing} size="small" sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}>
                  <CompassIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Fullscreen */}
              <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"} placement="right">
                <IconButton onClick={toggleFullscreen} size="small" sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}>
                  {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                </IconButton>
              </Tooltip>

              <Divider sx={{ borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'inherit' }} />

              {/* My Location */}
              <Tooltip title="My Location" placement="right">
                <IconButton
                  onClick={() => {
                    navigator.geolocation?.getCurrentPosition(
                      (pos) => {
                        mapRef.current?.flyTo({
                          center: [pos.coords.longitude, pos.coords.latitude],
                          zoom: 14,
                          duration: 1000
                        });
                      },
                      (err) => console.error('Geolocation error:', err)
                    );
                  }}
                  size="small"
                  sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}
                >
                  <MyLocationIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Map Style Selector */}
              <Tooltip title="Map Style" placement="right">
                <IconButton
                  onClick={(e) => setStyleAnchorEl(e.currentTarget)}
                  size="small"
                  sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}
                >
                  <SatelliteIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Divider sx={{ borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'inherit' }} />

              {/* Measure Distance */}
              <Tooltip title={isMeasuring ? "Cancel Measurement" : "Measure Distance"} placement="right">
                <IconButton
                  onClick={toggleMeasurement}
                  size="small"
                  sx={{
                    borderRadius: 0,
                    p: 1,
                    bgcolor: isMeasuring ? 'primary.main' : 'transparent',
                    color: isMeasuring ? 'white' : (theme.palette.mode === 'dark' ? 'white' : 'inherit'),
                    '&:hover': {
                      bgcolor: isMeasuring ? 'primary.dark' : 'action.hover',
                    },
                  }}
                >
                  <MeasureIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Clear Measurement */}
              {measurementPoints.length > 0 && (
                <Tooltip title="Clear Measurement" placement="right">
                  <IconButton
                    onClick={clearMeasurement}
                    size="small"
                    sx={{ borderRadius: 0, p: 1, color: 'error.main' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
                </>
              )}
            </Paper>

            {/* Map Style Popover */}
            {styleAnchorEl && (
              <Paper
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 60,
                  zIndex: 1001,
                  p: 2,
                  minWidth: 200,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Map Style
                </Typography>
                <Grid container spacing={1}>
                  {[
                    { value: 'streets', label: 'Streets' },
                    { value: 'satellite', label: 'Satellite' },
                    { value: 'satellite-streets', label: 'Hybrid' },
                    { value: 'dark', label: 'Dark' },
                    { value: 'light', label: 'Light' },
                    { value: 'outdoors', label: 'Outdoors' }
                  ].map((style) => (
                    <Grid item xs={6} key={style.value}>
                      <Card
                        onClick={() => {
                          setMapStyle(style.value as MapStyleType);
                          setStyleAnchorEl(null);
                        }}
                        sx={{
                          cursor: 'pointer',
                          border: mapStyle === style.value ? '2px solid #2196f3' : '1px solid #ddd',
                          '&:hover': { boxShadow: 2 }
                        }}
                      >
                        <Box sx={{ p: 1, textAlign: 'center' }}>
                          <Typography variant="caption">{style.label}</Typography>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ mt: 1, textAlign: 'right' }}>
                  <Typography
                    variant="caption"
                    sx={{ cursor: 'pointer', color: 'primary.main' }}
                    onClick={() => setStyleAnchorEl(null)}
                  >
                    Close
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* Measurement Line */}
            {measurementPoints.length >= 2 && (
              <Source
                id="measurement-line"
                type="geojson"
                data={{
                  type: 'Feature',
                  properties: {},
                  geometry: {
                    type: 'LineString',
                    coordinates: measurementPoints
                  }
                }}
              >
                <Layer
                  id="measurement-line-layer"
                  type="line"
                  paint={{
                    'line-color': '#ff6b35',
                    'line-width': 3,
                    'line-dasharray': [2, 2]
                  }}
                />
              </Source>
            )}

            {/* Measurement Points */}
            {measurementPoints.map((point, index) => (
              <Marker key={`measure-point-${index}`} longitude={point[0]} latitude={point[1]}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: '#ff6b35',
                    border: '2px solid white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  }}
                />
              </Marker>
            ))}

            {/* Measurement Result Display */}
            {measurementPoints.length >= 2 && totalDistance > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 40,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'rgba(255, 107, 53, 0.95)',
                  color: 'white',
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  zIndex: 1000,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  📏 {formatDistance(totalDistance)}
                </Typography>
              </Box>
            )}

            {/* Layer Controls Panel - Right Side Overlay */}
            <Paper
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                width: compactControls ? 220 : 300,
                maxHeight: 'calc(100% - 70px)',
                overflow: 'auto',
                zIndex: 1,
                bgcolor: (t) => alpha(t.palette.background.paper, 0.92),
                backdropFilter: 'blur(10px)',
                boxShadow: 3,
              }}
            >
              <Accordion defaultExpanded={!compactControls}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                  <Typography variant={compactControls ? 'body2' : 'subtitle1'} fontWeight="bold">
                    {compactControls ? 'Layers' : 'Map Controls & Layers'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0.5, pb: 1.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: compactControls ? 1 : 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={scrollWheelZoom}
                          onChange={(e) => setScrollWheelZoom(e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MouseIcon fontSize="small" />
                          <Typography variant="body2">Scroll-wheel zoom</Typography>
                        </Box>
                      }
                    />

                    {(filteredDwellLocations.length > 0 || heatmapData.length > 0 || Object.keys(deviceTracks || {}).length > 0) && (
                      <>
                        <Divider />
                        <Typography variant="subtitle2" fontWeight="bold">Data Layers</Typography>
                        <FormGroup>
                          {filteredDwellLocations.length > 0 && (
                            <FormControlLabel
                              control={<Switch size="small" checked={layers.dwellLocations} onChange={() => toggleLayer('dwellLocations')} />}
                              label={<Typography variant="body2">Dwell Locations</Typography>}
                            />
                          )}
                          {heatmapData.length > 0 && (
                            <FormControlLabel
                              control={<Switch size="small" checked={layers.heatmap} onChange={() => toggleLayer('heatmap')} />}
                              label={<Typography variant="body2">Activity Heatmap</Typography>}
                            />
                          )}
                          {Object.keys(deviceTracks || {}).length > 0 && (
                            <FormControlLabel
                              control={<Switch size="small" checked={layers.allDeviceTracks} onChange={() => toggleLayer('allDeviceTracks')} />}
                              label={<Typography variant="body2">Device Tracks</Typography>}
                            />
                          )}
                        </FormGroup>
                      </>
                    )}

                    {filteredRoutes.length > 0 && (
                      <>
                        <Divider />
                        <Typography variant="subtitle2" fontWeight="bold">Route Frequency</Typography>
                        <FormGroup>
                          {highFreqRoutes.length > 0 && (
                            <FormControlLabel
                              control={<Switch size="small" checked={layers.highFreqRoutes} onChange={() => toggleLayer('highFreqRoutes')} />}
                              label={<Typography variant="body2">High (10+) — {highFreqRoutes.length}</Typography>}
                            />
                          )}
                          {medFreqRoutes.length > 0 && (
                            <FormControlLabel
                              control={<Switch size="small" checked={layers.medFreqRoutes} onChange={() => toggleLayer('medFreqRoutes')} />}
                              label={<Typography variant="body2">Medium (5–9) — {medFreqRoutes.length}</Typography>}
                            />
                          )}
                          {lowFreqRoutes.length > 0 && (
                            <FormControlLabel
                              control={<Switch size="small" checked={layers.lowFreqRoutes} onChange={() => toggleLayer('lowFreqRoutes')} />}
                              label={<Typography variant="body2">Low (&lt;5) — {lowFreqRoutes.length}</Typography>}
                            />
                          )}
                        </FormGroup>
                      </>
                    )}

                    {temporalBreakdown && (temporalBreakdown.daytime_points > 0 || temporalBreakdown.nighttime_points > 0) && (
                      <>
                        <Divider />
                        <Typography variant="subtitle2" fontWeight="bold">Temporal Analysis</Typography>
                        <FormGroup>
                          {temporalBreakdown.daytime_points > 0 && (
                            <FormControlLabel
                              control={<Switch size="small" checked={layers.daytimeActivity} onChange={() => toggleLayer('daytimeActivity')} />}
                              label={<Typography variant="body2">Daytime ({temporalBreakdown.daytime_points})</Typography>}
                            />
                          )}
                          {temporalBreakdown.nighttime_points > 0 && (
                            <FormControlLabel
                              control={<Switch size="small" checked={layers.nighttimeActivity} onChange={() => toggleLayer('nighttimeActivity')} />}
                              label={<Typography variant="body2">Nighttime ({temporalBreakdown.nighttime_points})</Typography>}
                            />
                          )}
                        </FormGroup>
                      </>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Paper>

            {/* Heatmap Layer */}
            {layers.heatmap && heatmapGeoJSON.features.length > 0 && (
              <Source id="heatmap-source" type="geojson" data={heatmapGeoJSON}>
                <Layer
                  id="heatmap-layer"
                  type="heatmap"
                  paint={{
                    'heatmap-weight': ['get', 'intensity'],
                    'heatmap-intensity': 1,
                    'heatmap-color': [
                      'interpolate',
                      ['linear'],
                      ['heatmap-density'],
                      0, 'rgba(33,102,172,0)',
                      0.2, 'rgb(103,169,207)',
                      0.4, 'rgb(209,229,240)',
                      0.6, 'rgb(253,219,199)',
                      0.8, 'rgb(239,138,98)',
                      1, 'rgb(178,24,43)'
                    ],
                    'heatmap-radius': 20,
                    'heatmap-opacity': 0.7
                  }}
                />
              </Source>
            )}

            {/* Daytime Activity Heatmap Layer */}
            {layers.daytimeActivity && daytimeHeatmapGeoJSON.features.length > 0 && (
              <Source id="daytime-heatmap-source" type="geojson" data={daytimeHeatmapGeoJSON}>
                <Layer
                  id="daytime-heatmap-layer"
                  type="heatmap"
                  paint={{
                    'heatmap-weight': ['get', 'intensity'],
                    'heatmap-intensity': 1,
                    'heatmap-color': [
                      'interpolate',
                      ['linear'],
                      ['heatmap-density'],
                      0, 'rgba(255,255,0,0)',
                      0.2, 'rgba(255,220,0,0.4)',
                      0.4, 'rgba(255,195,0,0.6)',
                      0.6, 'rgba(255,165,0,0.7)',
                      0.8, 'rgba(255,140,0,0.8)',
                      1, 'rgba(255,100,0,0.9)'
                    ],
                    'heatmap-radius': 20,
                    'heatmap-opacity': 0.7
                  }}
                />
              </Source>
            )}

            {/* Nighttime Activity Heatmap Layer */}
            {layers.nighttimeActivity && nighttimeHeatmapGeoJSON.features.length > 0 && (
              <Source id="nighttime-heatmap-source" type="geojson" data={nighttimeHeatmapGeoJSON}>
                <Layer
                  id="nighttime-heatmap-layer"
                  type="heatmap"
                  paint={{
                    'heatmap-weight': ['get', 'intensity'],
                    'heatmap-intensity': 1,
                    'heatmap-color': [
                      'interpolate',
                      ['linear'],
                      ['heatmap-density'],
                      0, 'rgba(0,0,139,0)',
                      0.2, 'rgba(25,25,170,0.4)',
                      0.4, 'rgba(65,105,225,0.6)',
                      0.6, 'rgba(100,149,237,0.7)',
                      0.8, 'rgba(135,206,250,0.8)',
                      1, 'rgba(173,216,230,0.9)'
                    ],
                    'heatmap-radius': 20,
                    'heatmap-opacity': 0.7
                  }}
                />
              </Source>
            )}

            {/* Route Lines */}
            {routesGeoJSON.features.length > 0 && (
              <Source id="routes-source" type="geojson" data={routesGeoJSON}>
                <Layer
                  id="routes-layer"
                  type="line"
                  paint={{
                    'line-color': ['get', 'color'],
                    'line-width': 3,
                    'line-opacity': 0.7
                  }}
                />
              </Source>
            )}

            {/* Device Tracks */}
            {deviceTracksGeoJSON.features.length > 0 && (
              <Source id="device-tracks-source" type="geojson" data={deviceTracksGeoJSON}>
                <Layer
                  id="device-tracks-layer"
                  type="line"
                  paint={{
                    'line-color': '#9B59B6',
                    'line-width': 2,
                    'line-opacity': 0.6
                  }}
                />
              </Source>
            )}

            {/* Dwell Location Markers */}
            {layers.dwellLocations && filteredDwellLocations.map((location) => {
              const { color, size } = getDwellIconStyle(location.total_visits || 0);
              
              return (
                <Marker
                  key={location.id}
                  longitude={location.center_longitude}
                  latitude={location.center_latitude}
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    setSelectedDwell(location);
                  }}
                >
                  <div
                    style={{
                      backgroundColor: color,
                      border: '3px solid white',
                      borderRadius: '50%',
                      width: size,
                      height: size,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: size > 25 ? '12px' : '10px',
                      fontWeight: 'bold',
                      boxShadow: '0 3px 6px rgba(0,0,0,0.4)',
                      cursor: 'pointer'
                    }}
                  >
                    {location.total_visits || 0}
                  </div>
                </Marker>
              );
            })}

            {/* Dwell Location Popup — shared dark command card */}
            {selectedDwell && (
              <Popup
                longitude={selectedDwell.center_longitude}
                latitude={selectedDwell.center_latitude}
                onClose={() => setSelectedDwell(null)}
                closeButton={false}
                closeOnClick={false}
                anchor="bottom"
                className={DARK_MAP_POPUP_CLASS}
                maxWidth="320px"
                style={{ padding: 0, background: 'transparent', boxShadow: 'none' }}
              >
                <DarkMapPopupShell
                  accent="#f59e0b"
                  title={selectedDwell.place_name || 'Unknown Location'}
                  badge="DWELL"
                  onClose={() => setSelectedDwell(null)}
                >
                  <DarkMapPopupRow label="Visits">{selectedDwell.total_visits || 0}</DarkMapPopupRow>
                  {selectedDwell.total_duration ? (
                    <DarkMapPopupRow label="Dur.">
                      {(selectedDwell.total_duration / 60).toFixed(1)} hours
                    </DarkMapPopupRow>
                  ) : null}
                  {selectedDwell.address ? (
                    <DarkMapPopupRow label="Addr">{selectedDwell.address}</DarkMapPopupRow>
                  ) : null}
                  <DarkMapPopupCoords
                    lat={selectedDwell.center_latitude}
                    lon={selectedDwell.center_longitude}
                  />
                </DarkMapPopupShell>
              </Popup>
            )}
          </Map>
        </Box>
      </Card>
    </Box>
  );
};

export default EnhancedMapVisualizationGL;
